// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import styles from '@app/features/interest_onboarding/components/InterestOnboardingGate.module.css';
import type {InterestPickerProps} from '@app/features/interest_onboarding/components/InterestPicker';
import InterestOnboarding from '@app/features/interest_onboarding/state/InterestOnboarding';
import {isInterestGateOpen} from '@app/features/interest_onboarding/utils/InterestOnboardingUtils';
import {createNamedLoadableComponent} from '@app/features/platform/components/loadable/LoadableComponent';
import {useLocation} from '@app/features/platform/components/router/RouterReact';
import SessionManager from '@app/features/platform/state/AuthSession';
import UsernameSelection from '@app/features/user/state/UsernameSelection';
import Users from '@app/features/user/state/Users';
import {usernameGateMode} from '@app/features/user/utils/UsernameSelectionGateUtils';
import {DELETED_USER_ID} from '@fluxer/constants/src/UserConstants';
import {observer} from 'mobx-react-lite';
import type {ReactNode} from 'react';

const InterestPicker = createNamedLoadableComponent<InterestPickerProps>({
	displayName: 'InterestPicker',
	load: async () => (await import('@app/features/interest_onboarding/components/InterestPicker')).InterestPicker,
});

export function usernameModeFor(accountKey: string) {
	const currentUser = Users.currentUser;
	const user = currentUser?.id === SessionManager.userId ? currentUser : undefined;
	if (!user) return null;
	return usernameGateMode(
		user.id === DELETED_USER_ID.toString() ? undefined : user,
		RuntimeConfig.usernameMigration,
		Date.now(),
		UsernameSelection.requiredAccountKey === accountKey,
	);
}

// Sits inside the username gate, which always comes first; while the picker is not due it only watches one flag.
export const InterestOnboardingGate = observer(({children}: {children: ReactNode}) => {
	const {pathname} = useLocation();
	const accountKey = SessionManager.isAuthenticated ? SessionManager.currentAccountKey : null;
	const due = InterestOnboarding.isDue(accountKey);
	const usernameMode = due && accountKey !== null ? usernameModeFor(accountKey) : null;
	const open =
		usernameMode !== null &&
		isInterestGateOpen({dueAccountKey: InterestOnboarding.due?.accountKey ?? null, accountKey, usernameMode, pathname});
	return (
		<>
			<div className={styles.app} inert={open || undefined} data-flx="interest-onboarding.gate.app">
				{children}
			</div>
			{open && accountKey && (
				<section
					className={styles.screen}
					aria-labelledby="interest-onboarding-heading"
					data-flx="interest-onboarding.gate.screen"
				>
					<InterestPicker key={accountKey} accountKey={accountKey} />
				</section>
			)}
		</>
	);
});
